import { NotificationType } from '@iichi/domain/notification';
import { defineStore } from 'pinia';
import { computed, readonly, ref } from 'vue';
import * as model from '@/js/lib/api/v1/notification';
import { type NotificationItem } from '@/js/lib/api/v1/notification';

// グルーピング戦略: typeに対してどのフィールドをサブキーにするかを定義
// undefined = グルーピングしない、'buddy' = buddyMemberId、'link' = linkId、'none' = typeのみ（全件まとめる）
type GroupStrategy = 'buddy' | 'link' | 'none' | undefined;

// 値下げ系3種は同一グループキーに正規化する
const PRICE_OFF_TYPES = new Set<number>([NotificationType.PRICE_OFF_ITEM_IN_CART, NotificationType.PRICE_OFF_ITEM_FROM_FOLLOWING_SHOPS, NotificationType.PRICE_OFF_ITEM_IN_FAVORITES]);

function normalizedType(type: number): number {
  return PRICE_OFF_TYPES.has(type) ? NotificationType.PRICE_OFF_ITEM_IN_CART : type;
}

function groupStrategy(type: number): GroupStrategy {
  const t = normalizedType(type);
  switch (t) {
    case NotificationType.PRICE_OFF_ITEM_IN_CART: {
      return 'buddy';
    }
    case NotificationType.ITEM_LIKED: {
      return 'link';
    }
    case NotificationType.SHOP_FOLLOWED: {
      return 'none';
    }
    case NotificationType.MESSAGE_RECEIVED: {
      return 'buddy';
    }
    case NotificationType.ITEM_RESTOCK_REQUESTED: {
      return 'link';
    }
    default: {
      return undefined;
    }
  }
}

function groupSubKey(item: NotificationItem, strategy: GroupStrategy): string {
  switch (strategy) {
    case 'buddy': {
      return String(item.buddyMemberId ?? '');
    }
    case 'link': {
      return String(item.linkId ?? '');
    }
    case 'none': {
      return 'all';
    }
  }
  return '';
}

function isSameGroup(a: NotificationItem, b: NotificationItem): boolean {
  const strategy = groupStrategy(a.type);
  if (strategy === undefined) return false;
  if (normalizedType(a.type) !== normalizedType(b.type)) return false;
  return groupSubKey(a, strategy) === groupSubKey(b, strategy);
}

function groupSubject(item: NotificationItem): string {
  if (!item.subject) return '';
  const t = normalizedType(item.type);
  switch (t) {
    case NotificationType.ITEM_LIKED:
    case NotificationType.ITEM_RESTOCK_REQUESTED: {
      return item.subject;
    }
    default: {
      return `${item.subject}さん`;
    }
  }
}

export interface NotificationGroup {
  key: string;
  type: number;
  subject: string;
  items: NotificationItem[];
}

export type NotificationListItem = NotificationItem | NotificationGroup;

export const useNotificationStore = defineStore('notification', () => {
  const isLoaded = ref(false);
  const notifications = ref<NotificationItem[]>([]);
  const totalCount = ref(0);
  const updatedCount = ref(0);
  const expandedGroupKeys = ref<Set<string>>(new Set());

  const groupedNotifications = computed<NotificationListItem[]>(() => {
    const result: NotificationListItem[] = [];

    let i = 0;
    while (i < notifications.value.length) {
      const current = notifications.value[i];

      const strategy = groupStrategy(current.type);
      if (strategy === undefined) {
        result.push(current);
        i++;
        continue;
      }

      // buddy/none戦略でsubjectがない場合はグルーピングしない
      if ((strategy === 'buddy' || strategy === 'none') && !current.subject) {
        result.push(current);
        i++;
        continue;
      }

      // 連続する同種通知を収集
      const groupItems: NotificationItem[] = [current];
      while (i + groupItems.length < notifications.value.length && isSameGroup(current, notifications.value[i + groupItems.length])) {
        groupItems.push(notifications.value[i + groupItems.length]);
      }

      if (groupItems.length === 1) {
        result.push(current);
      } else {
        const typeKey = normalizedType(current.type);
        const subKey = groupSubKey(current, strategy);
        const key = `${typeKey}-${subKey}-${groupItems[0].id}`;
        if (expandedGroupKeys.value.has(key)) {
          result.push(...groupItems);
        } else {
          result.push({
            key,
            type: typeKey,
            subject: groupSubject(current),
            items: groupItems,
          });
        }
      }

      i += groupItems.length;
    }

    return result;
  });

  function expandGroup(key: string) {
    expandedGroupKeys.value = new Set([...expandedGroupKeys.value, key]);
  }

  async function loadNotifications({ limit = 20 } = {}) {
    const sinceId = notifications.value[notifications.value.length - 1]?.id;

    try {
      const res = await model.getNotifications({ sinceId, limit });
      notifications.value = [...notifications.value, ...res.notifications];
      totalCount.value = res.totalCount;
      isLoaded.value = true;
    } catch {
      notifications.value = [];
      totalCount.value = 0;
      isLoaded.value = true;
    }
  }

  async function markAsChecked() {
    if (notifications.value.length > 0) {
      const notificationId = notifications.value[0].id;
      await model.markAsConfirmed({ notificationId });
      updatedCount.value = 0;
    }
  }

  async function checkUpdated() {
    try {
      const res = await model.checkUpdated();
      updatedCount.value = res.updatedCount;
    } catch {
      updatedCount.value = 0;
    }
  }

  return {
    isLoaded: readonly(isLoaded),
    notifications,
    totalCount: readonly(totalCount),
    updatedCount: readonly(updatedCount),
    groupedNotifications,
    loadNotifications,
    markAsChecked,
    checkUpdated,
    expandGroup,
  };
});
